// src/pages/EditProfilePage.js
import React, { useContext, useState, useEffect } from "react";
import { LanguageContext } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { updateMe } from "../services/users.api";

function EditProfilePage() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    city: "",
    area: "",
    street: "",
    building: "",
  });

  const [phoneTouched, setPhoneTouched] = useState(false);

  /* ================= PHONE FORMAT (UNCHANGED LOGIC) ================= */

  const formatLbLocal = (digits) => {
    const d = digits.slice(0, 8);
    return [d.slice(0, 2), d.slice(2, 5), d.slice(5, 8)]
      .filter(Boolean)
      .join(" ");
  };

  const formatLbPlus961 = (digitsAfter961) => {
    const d = digitsAfter961.slice(0, 8);
    const parts = [d.slice(0, 2), d.slice(2, 5), d.slice(5, 8)]
      .filter(Boolean)
      .join(" ");
    return parts ? `+961 ${parts}` : "+961";
  };

  const formatPhoneInput = (raw) => {
    const v = (raw || "").trim();

    if (v.startsWith("+961")) {
      const digits = v.replace(/\D/g, "").slice(3);
      return formatLbPlus961(digits);
    }

    const first = v.replace(/\s/g, "").charAt(0);
    if (first === "0" || first === "7" || first === "8") {
      const digits = v.replace(/\D/g, "").slice(0, 8);
      return formatLbLocal(digits);
    }

    return raw; // international untouched
  };

  const validatePhone = (value) => {
    const v = (value || "").trim();

    if (v.startsWith("+961")) {
      const digits = v.replace(/\D/g, "").slice(3);
      return digits.length !== 8
        ? "Phone must be +961 followed by 8 digits."
        : null;
    }

    const compact = v.replace(/\D/g, "");
    if (/^[078]/.test(compact)) {
      return compact.length !== 8
        ? "Phone must be exactly 8 digits."
        : null;
    }

    return null;
  };

  /* ================= LOAD USER ================= */

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("esill-user"));
    if (!user) return;

    const rawPhone = user.phone || "";
    const rawEmail = user.email || "";
    const phoneLooksLikeEmail = rawPhone.includes("@");

    setForm({
      ...user,
      phone: phoneLooksLikeEmail ? "" : rawPhone,
      email: rawEmail || (phoneLooksLikeEmail ? rawPhone : ""),
    });
  }, []);

  const TEXT = {
    EN: {
      title: "EDIT PROFILE",
      save: "Save Changes",
      placeholders: {
        full_name: "Full Name",
        phone: "Phone Number",
        email: "Email",
        city: "City",
        area: "Area",
        street: "Street",
        building: "Building",
      },
    },
    AR: {
      title: "تعديل الملف",
      save: "حفظ التغييرات",
      placeholders: {
        full_name: "الاسم الكامل",
        phone: "رقم الهاتف",
        email: "البريد الإلكتروني",
        city: "المدينة",
        area: "المنطقة",
        street: "الشارع",
        building: "المبنى",
      },
    },
  };

  const t = TEXT[lang];

  const handleChange = (k, v) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setPhoneTouched(true);
    const phoneError = validatePhone(form.phone);
    if (phoneError) return;

    try {
      const updated = await updateMe({
        full_name: form.full_name,
        phone: form.phone,
        email: form.email,
        city: form.city,
        area: form.area,
        street: form.street,
        building: form.building,
      });

      localStorage.setItem("esill-user", JSON.stringify(updated));
      navigate("/account");
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div
      className="w-full min-h-screen bg-[#0F1A12] text-white pt-[95px] px-4 pb-24"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      {/* BACK */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-[80px] text-3xl opacity-70 hover:opacity-100"
        style={lang === "AR" ? { right: 16 } : { left: 16 }}
      >
        ×
      </button>

      {/* TITLE */}
      <h1 className="text-xl font-semibold text-center tracking-wide">
        {t.title}
      </h1>
      <div className="h-[2px] w-20 bg-[#2E5A2A] mx-auto mt-2 mb-6"></div>

      {/* FORM */}
      <div className="max-w-md mx-auto bg-[#121F17] border border-[#2E5A2A] rounded-lg p-5 space-y-4">
        <Field
          placeholder={t.placeholders.full_name}
          value={form.full_name}
          onChange={(v) => handleChange("full_name", v)}
        />

        <Field
          placeholder={t.placeholders.phone}
          value={form.phone}
          onChange={(v) => handleChange("phone", formatPhoneInput(v))}
          onBlur={() => setPhoneTouched(true)}
        />

        {phoneTouched && validatePhone(form.phone) && (
          <p className="text-xs text-red-400">
            {validatePhone(form.phone)}
          </p>
        )}

        <Field
          placeholder={t.placeholders.email}
          value={form.email}
          onChange={(v) => handleChange("email", v)}
        />

        <Field
          placeholder={t.placeholders.city}
          value={form.city}
          onChange={(v) => handleChange("city", v)}
        />

        <Field
          placeholder={t.placeholders.area}
          value={form.area}
          onChange={(v) => handleChange("area", v)}
        />

        <Field
          placeholder={t.placeholders.street}
          value={form.street}
          onChange={(v) => handleChange("street", v)}
        />

        <Field
          placeholder={t.placeholders.building}
          value={form.building}
          onChange={(v) => handleChange("building", v)}
        />

        <button
          onClick={save}
          className="
            w-full py-3 rounded-md
            bg-[#2E5A2A]
            font-semibold text-lg
            hover:brightness-110
            transition active:scale-[0.98]
          "
        >
          {t.save}
        </button>
      </div>
    </div>
  );
}

function Field({ placeholder, value, onChange, onBlur }) {
  return (
    <input
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      className="
        w-full px-4 py-3 rounded-md text-sm
        bg-[#0F1A12] text-white
        border border-[#2E5A2A]
        focus:outline-none
      "
    />
  );
}

export default EditProfilePage;
